import type { Level, OdbKind } from "./api/types";

/** Table des échelons : jumeau client de `server/generex_engine/echelons.py` (chantier « niveau Corps d'armée »,
 *  étape 2, registre B48, docs/08 §22), vérifié par le jeu de vecteurs `__fixtures__/echelons.json` régénéré par le
 *  serveur (`python -m generex_engine.echelons`). Un niveau de dossier est l'échelon qui émet l'ordre : COMPAGNIE <
 *  REGIMENT < BRIGADE < DIVISION < CORPS ; CENTAC, variante du niveau régiment, est hors hiérarchie. Le corps d'armée
 *  (CORPS, APP-6 22, docs/08 §23) est en service depuis l'étape 3 (terrain libre seulement) ; son ordre au format du
 *  STANAG 2014 et sa déclinaison aux divisions puis à leurs brigades depuis l'étape 4 (docs/08 §24). */

export interface Echelon {
  /** Niveau de dossier, échelon de l'émetteur. */
  key: string;
  /** Rang hiérarchique, croissant vers le haut. */
  order: number;
  /** Échelons APP-6 de l'unité de tête, le premier par défaut. */
  app6: readonly string[];
  /** Niveau d'état-major (MEL/MIL, PC et soutien) : la compagnie suit le GTIA. */
  staff: string;
  /** Nature d'ODB ami qui commande ce niveau. */
  odb_kind: string | null;
  /** Nature d'ODB ennemi face à ce niveau. */
  eni_odb_kind: string | null;
  /** Rang moteur de l'unité de tête d'un ODB ami. */
  rank: string;
  /** Rang moteur de l'unité de tête d'un ODB ennemi de même niveau. */
  eni_rank: string | null;
  /** Niveau du dossier d'un subordonné. */
  child: string | null;
  /** Emplacements de la manœuvre tenus par les subordonnés. */
  slots: number;
  /** Matériels majeurs comptés par défaut pour l'unité de tête. */
  quantity: number;
  /** Émetteur au féminin : « de la brigade », « de la division » ; sinon « du GTIA », « du S/GTIA ». */
  feminine: boolean;
  /** L'OPO de ce niveau porte des annexes. */
  annexes: boolean;
  /** Niveaux rédigés d'un geste par la déclinaison (0 : pas de déclinaison). */
  declined: number;
  /** false : place réservée, absente de toutes les tables dérivées. */
  active: boolean;
}

const row = (
  key: string, order: number, app6: readonly string[], staff: string, odb_kind: string | null,
  eni_odb_kind: string | null, rank: string, eni_rank: string | null, child: string | null, slots: number,
  quantity: number, feminine: boolean, annexes: boolean, declined: number, active = true,
): Echelon => ({ key, order, app6, staff, odb_kind, eni_odb_kind, rank, eni_rank, child, slots, quantity, feminine, annexes, declined, active });

export const TABLE: readonly Echelon[] = [
  row("COMPAGNIE", 0, ["15"], "gtia", null, null, "SGTIA", "Compagnie", null, 4, 12, false, false, 0),
  row("REGIMENT", 1, ["16", "17"], "gtia", "gtia", "eni_bat", "GTIA", "Bataillon", "COMPAGNIE", 3, 60, false, false, 1),
  row("BRIGADE", 2, ["18"], "brigade", "brigade", "eni_brig", "BRIGADE", "Brigade", "REGIMENT", 4, 200, true, false, 1),
  row("DIVISION", 3, ["21"], "division", "division", "eni_div", "DIVISION", "Division", "BRIGADE", 4, 780, true, true, 2),
  // Corps d'armée (docs/08 §23 et §24) : terrain libre seulement ; ordre au format du STANAG 2014 (annexes A à F),
  // décliné aux divisions puis à leurs brigades.
  row("CORPS", 4, ["22"], "corps", "corps", "eni_corps", "CORPS", "Corps", "DIVISION", 3, 2_400, false, true, 2),
];

export const BY_KEY: Readonly<Record<string, Echelon>> = Object.fromEntries(TABLE.map((e) => [e.key, e]));
/** Niveaux en service, du plus bas au plus haut. */
export const ACTIVE: readonly Echelon[] = TABLE.filter((e) => e.active).sort((a, b) => a.order - b.order);
export const ACTIVE_KEYS: readonly string[] = ACTIVE.map((e) => e.key);
/** Niveau le plus haut en service (le corps d'armée). */
export const TOP: Echelon = ACTIVE[ACTIVE.length - 1] as Echelon;

/** Niveaux acceptés par les routes de placement (fuseau, mesures de la ZO, emprise), dans l'ordre historique de l'API :
 *  un niveau ajouté au-dessus prend place après la division. Écrit en clair pour le typage, comparé à la table. */
export const PLACED_LEVELS = ["REGIMENT", "BRIGADE", "DIVISION", "CORPS", "COMPAGNIE"] as const;
export type PlacedLevel = (typeof PLACED_LEVELS)[number];
export const isPlacedLevel = (level: unknown): level is PlacedLevel =>
  typeof level === "string" && (PLACED_LEVELS as readonly string[]).includes(level);

/** Ligne d'un niveau en service (CENTAC : celle du régiment) ; undefined pour un niveau inconnu ou réservé. */
export function echelonOf(level: string | null | undefined): Echelon | undefined {
  const found = BY_KEY[level === "CENTAC" ? "REGIMENT" : (level ?? "")];
  return found?.active ? found : undefined;
}

/** Niveau du dossier d'un subordonné (DIVISION → BRIGADE → REGIMENT → COMPAGNIE). */
export function childLevel(level: string | null | undefined): string | null {
  return level === "CENTAC" ? null : (echelonOf(level)?.child ?? null);
}

/** Le niveau est-il au moins au rang de `floor` ? */
export function atLeast(level: string | null | undefined, floor: string): boolean {
  const found = echelonOf(level);
  return Boolean(found && found.order >= (BY_KEY[floor] as Echelon).order);
}

/** Niveaux d'émetteur déclinables, du plus haut au plus bas (brigade → GTIA, division → brigades puis GTIA, GTIA →
 *  S/GTIA). */
export const DECLINABLE: readonly Level[] = [...ACTIVE].reverse().filter((e) => e.declined > 0).map((e) => e.key as Level);

/** Niveaux d'où l'ordre se transmet par code à un subordonné (ceux qui ont un niveau subordonné et un ordre qui se
 *  décline, corps d'armée compris depuis l'étape 4), du bas vers le haut. */
export const TRANSMITTING: readonly Level[] = ACTIVE.filter((e) => e.child && e.odb_kind && e.declined > 0).map((e) => e.key as Level);

/** Nature d'ODB ami qui commande le niveau. */
export function odbKindOf(level: string | null | undefined): OdbKind | undefined {
  return (echelonOf(level)?.odb_kind ?? undefined) as OdbKind | undefined;
}

/** Natures d'ODB amis et ennemis en service, du plus bas au plus haut. */
export const FRIENDLY_KINDS = ACTIVE.flatMap((e) => (e.odb_kind ? [e.odb_kind as OdbKind] : []));
export const ENEMY_KINDS = ACTIVE.flatMap((e) => (e.eni_odb_kind ? [e.eni_odb_kind as OdbKind] : []));

/** Niveau commandé par une nature d'ODB (ami ou ennemi d'en face). */
export function levelOfKind(kind: string | null | undefined): Echelon | undefined {
  return ACTIVE.find((e) => e.odb_kind === kind || e.eni_odb_kind === kind);
}
